import React, { useState } from 'react';
import { Quote, Plus, Minus, Mail, Phone, MapPin, Linkedin, Twitter, Facebook, Instagram, CheckCircle2 } from 'lucide-react';
import { testimonials, faqs } from '../mock';
import { useToast } from '../hooks/use-toast';

export const Testimonials = () => (
  <section className="py-20 bg-[#f6f7fb]">
    <div className="max-w-7xl mx-auto px-5 lg:px-8">
      <h2 className="text-center text-[30px] md:text-[42px] font-bold text-slate-900">What Our Clients Say</h2>
      <p className="mt-4 text-center text-slate-600">Real stories from teams we've helped scale.</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-14">
        {testimonials.map((t, i) => (
          <div key={i} className="bg-white rounded-2xl p-8 border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.04)] hover:shadow-[0_16px_40px_rgba(0,0,0,0.08)] transition-shadow">
            <Quote className="w-9 h-9 text-[#E63946] mb-4" />
            <p className="text-slate-700 leading-relaxed text-[15px]">{t.text}</p>
            <div className="mt-6 flex items-center gap-3 pt-6 border-t border-slate-100">
              <div className="w-11 h-11 rounded-full bg-gradient-to-br from-red-400 to-pink-500 flex items-center justify-center text-white font-bold">{t.name.charAt(0)}</div>
              <div>
                <div className="font-semibold text-slate-900 text-sm">{t.name}</div>
                <div className="text-xs text-slate-500">{t.role}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export const FAQ = () => {
  const [open, setOpen] = useState(0);
  return (
    <section className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-5 lg:px-8">
        <h2 className="text-center text-[30px] md:text-[42px] font-bold text-slate-900">Frequently Asked Questions</h2>
        <p className="mt-4 text-center text-slate-600">Everything you need to know about our SaaS development services.</p>
        <div className="mt-12 space-y-4">
          {faqs.map((f, i) => (
            <div key={i} className={`border rounded-2xl overflow-hidden transition-all ${open === i ? 'border-[#E63946] shadow-[0_8px_30px_rgba(230,57,70,0.08)]' : 'border-slate-200'}`}>
              <button onClick={() => setOpen(open === i ? -1 : i)} className="w-full flex items-center justify-between px-6 py-5 text-left">
                <span className="font-semibold text-slate-900 text-[16px] pr-4">{i + 1}. {f.q}</span>
                {open === i ? <Minus className="w-5 h-5 text-[#E63946] flex-shrink-0" /> : <Plus className="w-5 h-5 text-slate-500 flex-shrink-0" />}
              </button>
              {open === i && (
                <div className="px-6 pb-5 text-slate-600 leading-relaxed text-[15px]">{f.a}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const ContactForm = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [sent, setSent] = useState(false);
  const submit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(`New Project Inquiry from ${form.name || 'Website Visitor'}`);
    const bodyLines = [
      `Name: ${form.name}`,
      `Email: ${form.email}`,
      `Phone: ${form.phone || 'N/A'}`,
      '',
      'Message:',
      form.message,
    ];
    const body = encodeURIComponent(bodyLines.join('\n'));
    window.location.href = `mailto:info@kamikatazicorp.com?subject=${subject}&body=${body}`;
    toast({ title: 'Opening your email...', description: 'Your message is ready to send to info@kamikatazicorp.com.' });
    setSent(true);
  };
  const reset = () => {
    setForm({ name: '', email: '', phone: '', message: '' });
    setSent(false);
  };
  return (
    <section id="contact" className="py-20" style={{ background: 'linear-gradient(135deg, #fde7ee 0%, #e8f2fc 100%)' }}>
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        <div className="bg-white rounded-3xl shadow-[0_20px_60px_rgba(0,0,0,0.08)] p-8 md:p-12 grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div>
            <h2 className="text-[28px] md:text-[36px] font-bold text-slate-900 leading-tight">Let's Build Something Great Together</h2>
            <p className="mt-4 text-slate-600 leading-relaxed">Have a project in mind? Reach out and our SaaS experts will get back to you within one business day.</p>
            <div className="mt-8 space-y-4">
              <a href="mailto:info@kamikatazicorp.com" className="flex items-center gap-3 text-slate-700 hover:text-[#E63946] transition-colors"><Mail className="w-5 h-5 text-[#E63946] flex-shrink-0" /> info@kamikatazicorp.com</a>
              <a
                href="tel:+17206134056"
                aria-label="Call Kamikatazi Corp at 720-613-4056"
                title="Tap to call 720-613-4056"
                className="group flex items-center gap-3 text-slate-700 hover:text-[#E63946] transition-colors"
              >
                <span className="w-9 h-9 rounded-full bg-red-50 group-hover:bg-[#E63946] flex items-center justify-center transition-colors">
                  <Phone className="w-4 h-4 text-[#E63946] group-hover:text-white transition-colors" />
                </span>
                <span className="flex flex-col leading-tight">
                  <span className="font-semibold">720-613-4056</span>
                  <span className="text-xs text-slate-500 group-hover:text-[#E63946]">Tap to call</span>
                </span>
              </a>
              <a
                href="https://www.google.com/maps/search/?api=1&query=2563+15th+Street+Ste+101+Denver+CO+80211"
                target="_blank"
                rel="noreferrer"
                className="flex items-start gap-3 text-slate-700 hover:text-[#E63946] transition-colors"
              >
                <MapPin className="w-5 h-5 text-[#E63946] flex-shrink-0 mt-0.5" />
                <span>2563 15th Street, Ste. 101<br />Denver, CO 80211</span>
              </a>
            </div>
            <div className="mt-6 rounded-2xl overflow-hidden border border-slate-200 shadow-sm">
              <iframe
                title="Kamikatazi Corp Office Location"
                src="https://www.google.com/maps?q=2563%2015th%20Street%2C%20Ste.%20101%20Denver%2C%20CO%2080211&output=embed"
                width="100%"
                height="220"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </div>
          <form onSubmit={submit} className={`space-y-4 ${sent ? 'hidden' : ''}`}>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your Name" className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#E63946] focus:outline-none focus:ring-2 focus:ring-red-100 transition-all" />
            <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email Address" className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#E63946] focus:outline-none focus:ring-2 focus:ring-red-100 transition-all" />
            <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone Number" className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#E63946] focus:outline-none focus:ring-2 focus:ring-red-100 transition-all" />
            <textarea required rows="4" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Tell us about your project..." className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#E63946] focus:outline-none focus:ring-2 focus:ring-red-100 transition-all resize-none" />
            <button type="submit" className="w-full bg-[#E63946] hover:bg-[#d02b38] text-white font-semibold py-3.5 rounded-xl transition-colors">Send Message</button>
          </form>
          {sent && (
            <div className="flex flex-col items-center justify-center text-center py-8 px-4 rounded-2xl bg-gradient-to-br from-red-50 via-white to-pink-50 border border-red-100">
              <div className="relative">
                <div className="absolute inset-0 rounded-full bg-red-100 blur-2xl opacity-70 animate-pulse" />
                <div className="relative w-20 h-20 rounded-full bg-white border-4 border-[#E63946] flex items-center justify-center shadow-lg">
                  <CheckCircle2 className="w-10 h-10 text-[#E63946]" strokeWidth={2.4} />
                </div>
              </div>
              <h3 className="mt-8 text-[28px] md:text-[32px] font-bold text-slate-900 leading-tight">Thanks — we'll be in touch</h3>
              <p className="mt-3 text-slate-600 max-w-sm leading-relaxed">
                Your message is on its way to <span className="font-semibold text-[#E63946]">info@kamikatazicorp.com</span>. A member of our team will get back to you within one business day.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3 w-full max-w-sm">
                <button onClick={reset} className="flex-1 bg-[#E63946] hover:bg-[#d02b38] text-white font-semibold py-3 rounded-xl transition-colors">Send Another Message</button>
                <a href="tel:+17206134056" className="flex-1 bg-white hover:bg-slate-50 text-slate-800 font-semibold py-3 rounded-xl border border-slate-200 transition-colors">Call Us Instead</a>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export const Footer = () => (
  <footer className="bg-slate-900 text-slate-300 pt-16 pb-8">
    <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-10">
      <div className="col-span-2">
        <div className="flex items-center gap-1 font-black text-2xl">
          <span className="text-[#E63946]">KAMIKATAZI</span>
          <span className="text-white">CORP</span>
        </div>
        <p className="mt-4 text-slate-400 text-sm leading-relaxed max-w-md">A trusted SaaS development company with 15+ years of experience building scalable, secure, AI-ready cloud applications for healthcare, fintech, retail, and more.</p>
        <div className="flex gap-3 mt-6">
          {[Linkedin, Twitter, Facebook, Instagram].map((Icon, i) => (
            <a key={i} href="#" className="w-9 h-9 rounded-full bg-slate-800 hover:bg-[#E63946] flex items-center justify-center transition-colors">
              <Icon className="w-4 h-4" />
            </a>
          ))}
        </div>
      </div>
      <div>
        <h4 className="text-white font-semibold mb-4">Services</h4>
        <ul className="space-y-2 text-sm">
          {['SaaS Development', 'Cloud Migration', 'API Development', 'Mobile Apps', 'UI/UX Design'].map((s) => (
            <li key={s}><a href="#" className="hover:text-[#E63946] transition-colors">{s}</a></li>
          ))}
        </ul>
      </div>
      <div>
        <h4 className="text-white font-semibold mb-4">Company</h4>
        <ul className="space-y-2 text-sm">
          {['About', 'Our Work', 'Blog', 'Careers', 'Contact'].map((s) => (
            <li key={s}><a href="#" className="hover:text-[#E63946] transition-colors">{s}</a></li>
          ))}
        </ul>
      </div>
    </div>
    <div className="max-w-7xl mx-auto px-5 lg:px-8 mt-12 pt-6 border-t border-slate-800 flex flex-col md:flex-row justify-between items-center gap-3 text-xs text-slate-500">
      <span>© {new Date().getFullYear()} Kamikatazi Corp. All rights reserved.</span>
      <div className="flex gap-6">
        <a href="#" className="hover:text-white">Privacy Policy</a>
        <a href="#" className="hover:text-white">Terms of Service</a>
      </div>
    </div>
  </footer>
);
